import React, { useState, useEffect } from "react";
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineClipboardDocumentList,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
} from "react-icons/hi2";
import { authService } from "../../../services";
import masterDataService from "../../../services/masterDataService";
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

// Synchronous cache loading function
const getCachedMaintenancePlanTasksData = () => {
  try {
    const cachedTasks = sessionStorage.getItem("maintenance_plan_tasks_data");
    const cachedPlans = sessionStorage.getItem("maintenance_plans_data");

    return {
      tasks: cachedTasks ? JSON.parse(cachedTasks) : [],
      plans: cachedPlans ? JSON.parse(cachedPlans) : [],
      hasAll: !!(cachedTasks && cachedPlans),
    };
  } catch (e) {
    console.error("Error parsing cached Maintenance Plan Tasks data:", e);
    return { tasks: [], plans: [], hasAll: false };
  }
};

const MaintenancePlanTasksPage = () => {
  // Initialize state with cached data if available (synchronous)
  const cachedInitialData = getCachedMaintenancePlanTasksData();
  const [tasks, setTasks] = useState(cachedInitialData.tasks);
  const [maintenancePlans, setMaintenancePlans] = useState(
    cachedInitialData.plans,
  );
  const [loading, setLoading] = useState(!cachedInitialData.hasAll);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    maintenancePlanId: "",
    sequenceNo: "",
    taskDescriptions: [""],
    expectedResult: "",
    inspectionFieldCode: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      // Load fresh data in background if we have cached data, otherwise show loading
      const hasCachedData = cachedInitialData.hasAll;
      loadData(!hasCachedData);
    } else {
      setError("Please login to view maintenance plan tasks");
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const loadData = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError("");
      const [tasksData, plansData] = await Promise.all([
        masterDataService.getMaintenancePlanTasks(tenantId),
        masterDataService.getMaintenancePlans(tenantId),
      ]);
      setTasks(tasksData || []);
      setMaintenancePlans(plansData || []);

      // Cache the data
      sessionStorage.setItem(
        "maintenance_plan_tasks_data",
        JSON.stringify(tasksData || []),
      );
      sessionStorage.setItem(
        "maintenance_plans_data",
        JSON.stringify(plansData || []),
      );
    } catch (err) {
      setError("Failed to load data. Please try again.");
      console.error("Error loading data:", err);
      // Don't clear cached data on error, keep showing it
      if (!cachedInitialData.hasAll) {
        setTasks([]);
        setMaintenancePlans([]);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredTasks = tasks.filter((task) =>
    task.taskDescription?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleAdd = () => {
    setEditingTask(null);
    setFormData({
      maintenancePlanId: "",
      sequenceNo: "",
      taskDescriptions: [""],
      expectedResult: "",
      inspectionFieldCode: "",
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      maintenancePlanId: task.maintenancePlanId?.toString() || "",
      sequenceNo: task.sequenceNo?.toString() || "",
      taskDescriptions: [task.taskDescription || ""],
      expectedResult: task.expectedResult || "",
      inspectionFieldCode: task.inspectionFieldCode || "",
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError("");
      await masterDataService.deleteMaintenancePlanTask(id);
      await loadData();
      setDeleteConfirm(null);
    } catch (err) {
      setError("Failed to delete maintenance plan task. Please try again.");
      console.error("Error deleting maintenance plan task:", err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.maintenancePlanId) {
      errors.maintenancePlanId = "Maintenance plan is required";
    }
    if (!formData.sequenceNo || isNaN(parseInt(formData.sequenceNo))) {
      errors.sequenceNo = "Sequence number is required and must be a number";
    }

    formData.taskDescriptions.forEach((desc, idx) => {
      if (!desc.trim()) {
        errors[`taskDescription_${idx}`] = "Task description is required";
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const baseSeq = parseInt(formData.sequenceNo);

      if (editingTask) {
        const taskData = {
          tenantId: tenantId,
          maintenancePlanId: parseInt(formData.maintenancePlanId),
          sequenceNo: baseSeq,
          taskDescription: formData.taskDescriptions[0].trim(),
          expectedResult: formData.expectedResult.trim() || null,
          inspectionFieldCode: formData.inspectionFieldCode.trim() || null,
        };
        taskData.taskId = editingTask.taskId;

        const updatePromise = masterDataService.updateMaintenancePlanTask(
          editingTask.taskId,
          taskData,
        );

        const createPromises = formData.taskDescriptions
          .slice(1)
          .filter((d) => d.trim())
          .map((desc, idx) => {
            return masterDataService.createMaintenancePlanTask({
              tenantId: tenantId,
              maintenancePlanId: parseInt(formData.maintenancePlanId),
              sequenceNo: baseSeq + idx + 1,
              taskDescription: desc.trim(),
              expectedResult: formData.expectedResult.trim() || null,
              inspectionFieldCode: formData.inspectionFieldCode.trim() || null,
            });
          });

        await Promise.all([updatePromise, ...createPromises]);
      } else {
        const createPromises = formData.taskDescriptions
          .filter((d) => d.trim())
          .map((desc, idx) => {
            return masterDataService.createMaintenancePlanTask({
              tenantId: tenantId,
              maintenancePlanId: parseInt(formData.maintenancePlanId),
              sequenceNo: baseSeq + idx,
              taskDescription: desc.trim(),
              expectedResult: formData.expectedResult.trim() || null,
              inspectionFieldCode: formData.inspectionFieldCode.trim() || null,
            });
          });
        await Promise.all(createPromises);
      }

      await loadData();
      setShowModal(false);
      setFormData({
        maintenancePlanId: "",
        sequenceNo: "",
        taskDescriptions: [""],
        expectedResult: "",
        inspectionFieldCode: "",
      });
      setEditingTask(null);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.errors ||
        err.message ||
        "Failed to save maintenance plan task. Please try again.";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTask(null);
    setFormData({
      maintenancePlanId: "",
      sequenceNo: "",
      taskDescriptions: [""],
      expectedResult: "",
      inspectionFieldCode: "",
    });
    setFormErrors({});
  };

  const getPlanName = (planId) => {
    if (!planId) return "-";
    const plan = maintenancePlans.find((p) => p.maintenancePlanId === planId);
    return plan ? `${plan.planCode} - ${plan.planName}` : "-";
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading maintenance plan tasks..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer  pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Maintenance Plan Tasks</h1>
          <p>Manage maintenance plan tasks for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add Task
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredTasks.length}{" "}
            {filteredTasks.length === 1 ? "task" : "tasks"}
          </div>
        </div>

        {filteredTasks.length === 0 ? (
          <div className="empty-state">
            <HiOutlineClipboardDocumentList className="empty-icon" />
            <h3>No tasks found</h3>
            <p>
              {searchTerm
                ? "Try adjusting your search terms"
                : "Get started by creating your first maintenance plan task"}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Task
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Plan</th>
                <th>Sequence</th>
                <th>Task Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map((task) => (
                <tr key={task.taskId}>
                  <td>{task.taskId}</td>
                  <td>{getPlanName(task.maintenancePlanId)}</td>
                  <td>{task.sequenceNo}</td>
                  <td className="name-cell">{task.taskDescription}</td>

                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(task)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(task)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingTask ? "Edit Maintenance Plan Task" : "Add New Maintenance Plan Task"}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="plan-task-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingTask
                  ? "Updating..."
                  : "Creating..."
                : editingTask
                ? "Update"
                : "Create"}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="plan-task-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="maintenancePlanId">
                Maintenance Plan <span className="required">*</span>
              </label>
              <select
                id="maintenancePlanId"
                value={formData.maintenancePlanId}
                onChange={(e) =>
                  setFormData({ ...formData, maintenancePlanId: e.target.value })
                }
                className={formErrors.maintenancePlanId ? "error" : ""}
              >
                <option value="">Select Maintenance Plan</option>
                {maintenancePlans.map((plan) => (
                  <option key={plan.planId} value={plan.planId.toString()}>
                    {plan.planName}
                  </option>
                ))}
              </select>
              {formErrors.maintenancePlanId && (
                <span className="field-error">
                  {formErrors.maintenancePlanId}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="sequenceNo">
                Sequence No <span className="required">*</span>
              </label>
              <input
                type="number"
                id="sequenceNo"
                value={formData.sequenceNo}
                onChange={(e) =>
                  setFormData({ ...formData, sequenceNo: e.target.value })
                }
                className={formErrors.sequenceNo ? "error" : ""}
                placeholder="Enter sequence number"
              />
              {formErrors.sequenceNo && (
                <span className="field-error">{formErrors.sequenceNo}</span>
              )}
            </div>

            <div className="form-group">
              <label>Task Descriptions</label>
              {formData.taskDescriptions.map((desc, index) => (
                <div key={index} style={{ marginBottom: "12px" }}>
                  <textarea
                    value={desc}
                    onChange={(e) => {
                      const newDescs = [...formData.taskDescriptions];
                      newDescs[index] = e.target.value;
                      setFormData({ ...formData, taskDescriptions: newDescs });
                    }}
                    placeholder={`Enter task description ${index + 1}`}
                    rows="2"
                    style={{ marginBottom: "4px" }}
                  />
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    {index === formData.taskDescriptions.length - 1 ? (
                      <button
                        type="button"
                        onClick={() => {
                          if (desc.trim()) {
                            setFormData({
                              ...formData,
                              taskDescriptions: [
                                ...formData.taskDescriptions,
                                "",
                              ],
                            });
                          }
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          background: "transparent",
                          border: "none",
                          cursor: desc.trim() ? "pointer" : "default",
                          opacity: desc.trim() ? 1 : 0.5,
                          padding: "6px 12px",
                          color: "#374151",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "28px",
                            height: "28px",
                            border: "1px solid #d1d5db; border-color: var(--border-primary)",
                            borderRadius: "6px",
                            background: "#fff",
                          }}
                        >
                          <HiOutlinePlus size={18} color="#000" />
                        </div>
                        Add Description
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          const newDescs = [...formData.taskDescriptions];
                          newDescs.splice(index, 1);
                          setFormData({
                            ...formData,
                            taskDescriptions: newDescs,
                          });
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "6px 12px",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          color: "#dc3545",
                        }}
                        title="Remove Description"
                      >
                        <HiOutlineTrash size={20} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="form-group">
              <label htmlFor="expectedResult">Expected Result</label>
              <textarea
                id="expectedResult"
                value={formData.expectedResult}
                onChange={(e) =>
                  setFormData({ ...formData, expectedResult: e.target.value })
                }
                placeholder="Enter expected result (optional)"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="inspectionFieldCode">
                Inspection Field Code
              </label>
              <input
                type="text"
                id="inspectionFieldCode"
                value={formData.inspectionFieldCode}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    inspectionFieldCode: e.target.value,
                  })
                }
                placeholder="Enter inspection field code (optional)"
              />
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.taskId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>Are you sure you want to delete this task?</p>
          <p className="warning-text">This action cannot be undone.</p>
        </div>
      </SharedModal>
    </div>
  );
};

export default MaintenancePlanTasksPage;
